import React, { useState, useRef, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { showSuccess, showError } from "toast/toast";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from 'swiper';
import { Navigation, Thumbs } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import imageCustomSquare from "assets/images/imagecustomrounded.png";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Product, Brand, Category } from 'types';
import { Link } from "react-router-dom";
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
import { getAuthCookie } from "../../utils/authCookies";
interface Installer {
  supplier_id: string;
  name: string;
  email: string;
}

const RealtorViewAgentProduct = () => {
  const { productId } = useParams<{ productId: string }>();
  const { agentId } = useParams<{ agentId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [formSubmitting, setFormSubmitting] = useState(false);
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const [productImages, setProductImages] = useState<any[]>([]);
  const [preview, setPreview] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isTeammember = location.pathname.includes('/teammember');
  const baseRoute = isTeammember ? '/teammember' : '/realtor';
  const [brands, setBrands] = useState<Brand[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [product, setProduct] = useState<Product>();
  const [installers, setInstallers] = useState<Installer[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef<SwiperType | null>(null);
  // Fetch product data
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/agent/${agentId}/inventory/${productId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch category data");
        }
        const data = await response.json();
        console.log(data);
        setProduct(data.product);
        setProductImages(data.product.images ? data.product.images : []);
      } catch (err) {
        console.error("Error fetching category data:", err);
        showError("Error fetching category data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    if (productId) fetchProduct();
  }, [productId, token]);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/brands`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const data = await response.json();
        setBrands(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchBrands();
  }, [token]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/categories`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        setCategories(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [token]);

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  useEffect(() => {
    const fetchInstallers = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/agent/suppliers`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch installers data");
        }

        const data = await response.json();
        console.log("Fetched Installers:", data);

        if (Array.isArray(data)) {
          setInstallers(data);
        } else {
          console.error("Unexpected installers data format:", data);
        }
      } catch (error) {
        console.error("Error fetching installers data:", error);
      }
    };

    fetchInstallers();
  }, [token]);


  const uploadFile = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("uploadDir", "products");

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${process.env.REACT_APP_API_URL}/images/upload`, true);
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);

      xhr.onload = () => {
        console.log("xhr:", xhr);
        if (xhr.readyState === 4) {
          const response = JSON.parse(xhr.responseText);
          console.log("upload response:", response);

          // Construct the full image URL using the base URL and `path` field
          const imageUrl = `${response.path}`;
          resolve(imageUrl);
        } else {
          reject(new Error("Failed to upload file"));
        }
      };

      xhr.onerror = () => reject(new Error("Network error during file upload"));
      xhr.send(formData);
    });
  };

  function getInstallerById(installerId: string) {
    console.log("Looking up installer for ID:", installerId);
    return installers?.find((installer) => installer.supplier_id === installerId) || null;
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: product?.name || "",
      description: product?.description || "",
      sku: product?.sku || "",
      brand: product?.brand || "",
      category: product?.category || "",
      item_condition: product?.item_condition || "",
      purchase_price: product?.purchase_price || 0,
      retail_price: product?.retail_price || 0,
      length: product?.package_dimensions?.length || 0,
      height: product?.package_dimensions?.height || 0,
      width: product?.package_dimensions?.width || 0,
      weight: product?.package_dimensions?.weight || 0,
      qty: product?.variants?.[0]?.inventory?.[0]?.available || 0,
      threshold_qty: product?.threshold_qty || 0,
      installerId: product?.linked_installer || "",
      notes: product?.notes || "",
      images: null, // Initialize as null for new uploads
      imageUrl: product?.image?.url || null, // Use this for prefilled image URL
      statuss: product?.status || "Active",
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .min(2, "Name must be at least 2 characters long")
        .max(50, "Name cannot be longer than 50 characters")
        .required("Name is required"),
      statuss: Yup.string().required("Status is required"),
    }),
    onSubmit: async (values) => {
      console.log("Submitting form...", values);
      setFormSubmitting(false);
    },
  });
  const idOf = (x?: any): string => (typeof x === "string" ? x : (x?.url ?? "")) || "";
  const buildImageUrl = (raw?: string) => {
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw)) return raw;
    const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
    const path = String(raw).replace(/\\/g, "/").replace(/^\/+/, "");
    return `${base}/${encodeURI(path)}`;
  };
  const productImageIds = React.useMemo(
    () => (productImages || []).map((img: any) => idOf(img)).filter(Boolean),
    [productImages]
  );

  const dataForMap = React.useMemo(() => {
    if (!productImageIds.length) return [];
    return [{ product: { variants: [{ images: productImageIds.map((url) => ({ url })) }] } }];
  }, [productImageIds]);

  const imageUrlMap = useVariantImageUrlMap(dataForMap, token);

  // 🔧 bump when map content changes (even if the object ref is stable)
  const mapVersion = React.useMemo(
    () => (imageUrlMap ? Object.keys(imageUrlMap).length : 0),
    [imageUrlMap]
  );

  // ✅ will re-run when productImages change OR when map gets populated
  const resolvedImages = React.useMemo(
    () =>
      (productImages || []).map((img: any) => {
        const id = idOf(img);
        return {
          src: getImageSrc(id, imageUrlMap) || buildImageUrl(id) || imageCustomSquare,
          alt: img?.alt_text || "",
        };
      }),
    [productImages, mapVersion]   // <-- changed here
  );
  const galleryImages = resolvedImages.length
    ? resolvedImages
    : [{ src: imageCustomSquare, alt: "No image" }];


  //   if (!isLoaded) return <div>Loading...</div>;

  return (
    <div className="content">
      <div className="d-flex justify-content-between">
        <h2 className="ps-4">Product View</h2>
        {/* <Link
          to={`${baseRoute}/agent/${agentId}/editAgentProduct/${productId}`}
          className="border-0 p-0"
        >
          <i className="ri-pencil-fill fs-24"></i>
        </Link> */}
      </div>
      {/* <h2 className="ps-4">Product Details </h2> */}
      <div className="row justify-content-center">
        <div className="col-md-12 col-lg-12 col-xl-12">
          <div className="card mt-1 card-bg-fill">
            <div className="card-body p-4">
              <div className="p-2 mt-1">
                <div className="row">
                  <div className="col-lg-5">
                    <div className="gall-card shadow-lg">
                      <div className="gall-main">
                        <Swiper
                          onSwiper={(s: any) => (swiperRef.current = s)}
                          onSlideChange={(s: any) => setActiveIndex(s.activeIndex)}
                          slidesPerView={1}
                          spaceBetween={8}
                          className="gall-swiper"
                        >
                          {galleryImages.map((img, i) => (
                            <SwiperSlide key={i}>
                              <img
                                className="gall-img"
                                src={img.src}
                                alt={img.alt || `Image ${i + 1}`}
                                onError={(e) => {
                                  e.currentTarget.src = imageCustomSquare;
                                }}
                              />
                            </SwiperSlide>
                          ))}
                        </Swiper>
                      </div>

                      <div className="gall-thumbs">
                        {galleryImages.map((img, i) => (
                          <button
                            key={i}
                            type="button"
                            className={`gall-thumb ${i === activeIndex ? "is-active" : ""}`}
                            onClick={() => swiperRef.current?.slideTo(i)}
                          >
                            <img
                              src={img.src}
                              alt={`Thumb ${i + 1}`}
                              onError={(e) => {
                                e.currentTarget.src = imageCustomSquare;
                              }}
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-7">
                    <div className="row">
                      <div className="col-md-9 mb-3">
                        <label className="form-label table-cells">
                          Product Name{" "}

                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="name"
                          name="name"
                          value={product?.name}
                          readOnly
                        />
                      </div>
                      <div className="col-md-3 mb-3">
                        <label className="form-label table-cells">
                          SKU
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="sku"
                          name="sku"
                          value={formik.values.sku}
                          readOnly
                        />
                      </div>
                      <div className="col-md-3 mb-3">
                        <label className="form-label table-cells">Brand</label>
                        <input
                          className="form-control"
                          id="brand"
                          name="brand"
                          value={product?.brand}
                          readOnly
                        />
                      </div>
                      <div className="col-md-3 mb-3">
                        <label className="form-label table-cells">Category</label>
                        <input type="text" className="form-control"
                          value={product?.category} readOnly />

                      </div>
                      <div className="col-md-3 mb-3">
                        <label className="form-label table-cells">
                          Item Condition
                        </label>
                        <input type="text"
                          className="form-control"
                          id="item_condition"
                          name="item_condition"
                          value={product?.item_condition}
                          readOnly
                        />
                      </div>

                      <div className="col-md-3 mb-3">
                        <label className="form-label table-cells">
                          Quantity{" "}

                        </label>
                        <input
                          type="number"
                          className="form-control"
                          id="qty"
                          name="qty"
                          value={formik.values.qty}
                          readOnly
                        />

                      </div>
                      <div className="col-md-3 mb-3">
                        <label className="form-label table-cells">
                          Threshold Quantity{" "}

                        </label>
                        <input
                          type="number"
                          className="form-control"
                          id="threshold_qty"
                          name="threshold_qty"

                          value={product?.threshold_qty}
                          readOnly
                        />

                      </div>
                      <div className="col-md-3 mb-3">
                        <label className="form-label table-cells">
                          Status
                        </label>
                        <input
                          className="form-control"
                          id="statuss"
                          name="statuss"

                          value={product?.status}
                          readOnly
                        />

                      </div>

                      <div className="col-md-12 mb-3">
                        <label className="form-label table-cells">
                          Product Description/Notes{" "}

                        </label>
                        <textarea
                          className="form-control product-description-textarea"
                          id="description"
                          name="description"

                          value={product?.description}
                          rows={4}
                          readOnly
                        ></textarea>

                      </div>

                      <div className="col-md-12 mb-3">
                        <label className="form-label table-cells">
                          Dimensions/Measurements (Inches)
                        </label>
                        <div className="row">
                          <div className="col-md-4">
                            <input
                              type="text"
                              className="form-control"
                              id="length"
                              name="length"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={product?.package_dimensions?.length}
                              placeholder="Length"
                              readOnly
                            />

                          </div>
                          <div className="col-md-4">
                            <input
                              type="text"
                              className="form-control"
                              id="height"
                              name="height"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={product?.package_dimensions?.height}
                              placeholder="Height"
                              readOnly
                            />

                          </div>
                          <div className="col-md-4">
                            <input
                              type="text"
                              className="form-control"
                              id="width"
                              name="width"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={product?.package_dimensions?.width}
                              placeholder="Width"
                              readOnly
                            />

                          </div>
                        </div>
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label table-cells">
                          Weight(in gms)
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="weight"
                          name="weight"

                          value={product?.package_dimensions?.weight}
                          readOnly
                        />

                      </div>


                      <div className="col-md-4 mb-3">
                        <label className="form-label table-cells">Installer</label>
                        <input
                          name="installerId"
                          id="installerId"
                          className="form-control"

                          value={product?.linked_installer || ""}
                          readOnly
                        />

                      </div>

                      <div className="col-md-12 mb-3">
                        <label className="form-label table-cells">
                          Notes for the Installer{" "}
                        </label>
                        <textarea
                          className="form-control"
                          id="notes"
                          name="notes"

                          value={product?.notes}
                          readOnly
                        ></textarea>

                      </div>
                      <div className="row mb-3 rcust-margin-4">
                        <div className="col-md-12 d-flex justify-content-between ms-3" id="row-overlap">
                          <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="btn back-btn surface-btn fw-bold fs-16"
                          >
                            <i className="bi bi-chevron-left me-2" />
                            Back
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealtorViewAgentProduct;
